import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { aAuMoins, useSession, type Role } from './sessionStore';

/** Ecran d'attente pendant la restauration de session : evite un flash de page de connexion. */
function Patience() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-fond">
      <span className="sr-only">Chargement de votre session…</span>
      <span
        className="size-8 animate-spin rounded-full border-2 border-bordure border-t-accent"
        aria-hidden
      />
    </div>
  );
}

/**
 * Garde des routes authentifiees.
 *
 * Trois cas, dans cet ordre :
 *  1. restauration en cours -> on patiente. Rediriger ici afficherait la page de
 *     connexion a quelqu'un qui a une session valide, juste avant de le renvoyer
 *     dans l'application : un aller-retour visible et desagreable ;
 *  2. non connecte -> connexion, en memorisant la destination voulue ;
 *  3. connecte sans entreprise active -> ecran de choix ou de creation.
 */
export function GardeAuthentifie({ children }: { children: ReactNode }) {
  const utilisateurId = useSession((etat) => etat.utilisateurId);
  const entrepriseActive = useSession((etat) => etat.entrepriseActive);
  const restaurationEnCours = useSession((etat) => etat.restaurationEnCours);
  const emplacement = useLocation();

  if (restaurationEnCours) {
    return <Patience />;
  }

  if (!utilisateurId) {
    return <Navigate to="/connexion" replace state={{ retour: emplacement.pathname }} />;
  }

  // Les routes de gestion d'entreprise restent accessibles sans entreprise active :
  // c'est precisement la qu'on en cree ou en choisit une.
  const routeDEntreprise = emplacement.pathname.startsWith('/entreprise');

  if (!entrepriseActive && !routeDEntreprise) {
    return <Navigate to="/entreprise/choisir" replace />;
  }

  return <>{children}</>;
}

/**
 * Garde de role.
 *
 * Second niveau seulement : l'API refuse de toute facon l'action. Cette garde
 * evite d'afficher une page dont chaque bouton echouerait — l'interface ne doit
 * jamais proposer ce que l'API refuserait (§12).
 */
export function GardeRole({ requis, children }: { requis: Role; children: ReactNode }) {
  const role = useSession((etat) => etat.role);

  if (!aAuMoins(role, requis)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/** Inverse : renvoie dans l'application quelqu'un de deja connecte. */
export function GardeAnonyme({ children }: { children: ReactNode }) {
  const utilisateurId = useSession((etat) => etat.utilisateurId);
  const restaurationEnCours = useSession((etat) => etat.restaurationEnCours);

  if (restaurationEnCours) {
    return <Patience />;
  }

  if (utilisateurId) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
